import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRemoteContent } from "@/lib/ContentContext";

const NEWS_CANVAS_MIN_HEIGHT = 520;
const NEWS_CANVAS_EDGE = 16;

function sanitizeFrontendRichHtml(html: string) {
  if (!html.includes("data-media-drag-handle") && !html.includes("data-media-resize-handle")) return html;
  if (typeof document === "undefined") return html;
  const container = document.createElement("div");
  container.innerHTML = html;
  container.querySelectorAll("[data-media-drag-handle='1'], [data-media-resize-handle='1']").forEach((node) => node.remove());
  return container.innerHTML;
}

function bindFrontendVideos(root: HTMLElement) {
  root.querySelectorAll("video").forEach((video) => {
    video.controls = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.setAttribute("x5-playsinline", "true");
    video.preload = "metadata";
  });
}

export default function NewsDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const siteContent = useRemoteContent();
  const [lang, setLang] = useState<"zh" | "en">("zh");
  const contentRef = useRef<HTMLElement | null>(null);
  const renderedHtmlRef = useRef("");
  const article = siteContent.newsArticles.find((item) => item.id === id);
  const contentHtml = sanitizeFrontendRichHtml(lang === "zh" ? article?.zhContentHtml ?? "" : article?.enContentHtml ?? "");

  const syncCanvasLayout = () => {
    const root = contentRef.current;
    if (!root) return;
    root.querySelectorAll("[data-media-drag-handle='1'], [data-media-resize-handle='1']").forEach((node) => node.remove());
    const mediaBlocks = Array.from(root.querySelectorAll("[data-media-block='1']")) as HTMLElement[];
    if (mediaBlocks.length === 0) {
      root.style.minHeight = `${NEWS_CANVAS_MIN_HEIGHT}px`;
      return;
    }

    const canvasWidth = root.clientWidth;
    let maxBottom = NEWS_CANVAS_MIN_HEIGHT;

    mediaBlocks.forEach((block) => {
      block.style.position = "absolute";
      block.style.margin = "0";
      block.style.maxWidth = "none";
      block.style.minWidth = "120px";
      block.style.minHeight = "90px";
      block.style.overflow = "visible";

      const media = block.querySelector("img, video, iframe") as HTMLImageElement | HTMLVideoElement | HTMLIFrameElement | null;
      if (media) {
        media.style.width = "100%";
        media.style.height = "100%";
        media.style.display = "block";
        if (media.tagName !== "IFRAME") media.style.objectFit = "contain";
      }

      const left = Math.max(NEWS_CANVAS_EDGE, parseFloat(block.style.left || `${NEWS_CANVAS_EDGE}`));
      const top = Math.max(NEWS_CANVAS_EDGE, parseFloat(block.style.top || `${NEWS_CANVAS_EDGE}`));
      const rawWidth = parseFloat(block.style.width || `${block.getBoundingClientRect().width || 320}`);
      const rawHeight = parseFloat(block.style.height || `${block.getBoundingClientRect().height || 180}`);
      const maxWidth = Math.max(120, canvasWidth - left - NEWS_CANVAS_EDGE);
      const width = Math.min(rawWidth, maxWidth);
      const height = Math.max(90, rawHeight);

      block.style.left = `${left}px`;
      block.style.top = `${top}px`;
      block.style.width = `${width}px`;
      block.style.height = `${height}px`;

      maxBottom = Math.max(maxBottom, top + height + NEWS_CANVAS_EDGE);
    });

    root.style.minHeight = `${Math.ceil(maxBottom)}px`;
  };

  useEffect(() => {
    const storedLang = localStorage.getItem("site-lang");
    if (storedLang === "en" || storedLang === "zh") setLang(storedLang);
  }, []);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    if (renderedHtmlRef.current !== contentHtml) {
      root.innerHTML = contentHtml;
      renderedHtmlRef.current = contentHtml;
    }
    bindFrontendVideos(root);
    syncCanvasLayout();
  }, [contentHtml]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => syncCanvasLayout());
    observer.observe(root);
    return () => observer.disconnect();
  }, [contentHtml]);

  if (!article) {
    return <div className="min-h-screen bg-[#eef1f3] text-black flex items-center justify-center">新闻未找到</div>;
  }

  return (
    <div className="min-h-screen bg-[#f3f4f2] text-black">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-5 md:py-8">
        <button onClick={() => navigate("/news")} className="text-sm text-[#5a5a5a] hover:text-black mb-8 cursor-pointer">
          {lang === "zh" ? "返回新闻发布" : "Back to News"}
        </button>
        <article className="overflow-hidden border border-black/10 bg-white text-black">
          <div className="grid grid-cols-1 lg:grid-cols-[120px_minmax(0,1fr)] gap-0 border-b border-black/10">
            <div className="px-6 py-4 md:px-8 lg:px-9 text-xs tracking-[0.24em] uppercase text-[#777777] border-b lg:border-b-0 lg:border-r border-black/10">
              {String(article.order).padStart(2, "0")}
            </div>
            <div className="px-6 py-4 md:px-8 lg:px-9">
              <div className="flex min-w-0 items-center gap-3 text-sm leading-6">
                <div className="shrink-0 text-[11px] uppercase tracking-[0.24em] text-[#7b7b7b]">
                  {lang === "zh" ? "Document Entry" : "Document Entry"}
                </div>
                <h1 className="min-w-0 shrink text-sm font-semibold text-[#222222] truncate">{lang === "zh" ? article.zhTitle : article.enTitle}</h1>
                <p className="min-w-0 flex-1 text-sm text-[#4b4b4b] truncate">{lang === "zh" ? article.zhSummary : article.enSummary}</p>
              </div>
            </div>
          </div>

          <div className="px-6 py-7 md:px-8 md:py-8 lg:px-9">
            <article
              ref={contentRef}
              style={{ minHeight: `${NEWS_CANVAS_MIN_HEIGHT}px` }}
              className="relative min-h-[520px] overflow-visible p-4 text-[15px] leading-8 text-[#222] [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:leading-9 [&_h1]:text-black [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:leading-8 [&_h2]:text-black [&_h3]:text-base [&_h3]:font-semibold [&_h3]:leading-8 [&_h3]:text-black [&_p]:my-0 [&_p]:leading-8 [&_ul]:my-0 [&_ul]:pl-6 [&_ol]:my-0 [&_ol]:pl-6 [&_li]:leading-8 [&_blockquote]:my-0 [&_blockquote]:border-l-2 [&_blockquote]:border-black/10 [&_blockquote]:pl-4 [&_a]:text-black [&_strong]:text-black [&_hr]:border-black/10 [&_[data-media-block='1']]:absolute [&_[data-media-block='1']]:m-0 [&_[data-media-block='1']]:max-w-none [&_[data-media-block='1']]:overflow-visible [&_[data-media-drag-handle='1']]:hidden [&_[data-media-resize-handle='1']]:hidden [&_[data-media-block='1']_img]:block [&_[data-media-block='1']_img]:w-full [&_[data-media-block='1']_img]:h-full [&_[data-media-block='1']_img]:object-contain [&_[data-media-block='1']_video]:block [&_[data-media-block='1']_video]:w-full [&_[data-media-block='1']_video]:h-full [&_[data-media-block='1']_video]:max-w-none [&_[data-media-block='1']_video]:bg-black [&_[data-media-block='1']_video]:object-contain [&_[data-media-block='1']_iframe]:block [&_[data-media-block='1']_iframe]:w-full [&_[data-media-block='1']_iframe]:h-full [&_[data-media-block='1']_iframe]:max-w-none [&_[data-media-block='1']_iframe]:bg-black"
            />
          </div>
        </article>
      </div>
    </div>
  );
}
